import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaCheckCircle,
  FaChartLine,
  FaCreditCard,
  FaCloud,
  FaCog,
  FaEdit,
  FaHourglassHalf,
  FaLayerGroup,
  FaEnvelope,
  FaMoneyBillWave,
  FaMoon,
  FaPause,
  FaPlay,
  FaPowerOff,
  FaSave,
  FaSun,
  FaSync,
  FaTag,
  FaTasks,
  FaTrash,
  FaTimesCircle,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { createTenant, deleteTenant, updateTenant } from "../api/tenants";
import {
  approveCancellationRequest,
  cancelSubscriptionDirect,
  getCancellationRequests,
  rejectCancellationRequest,
} from "../api/payments";
import {
  createCoupon,
  getDiscountConfig,
  updateCouponStatus,
  updateReferralPercent,
} from "../api/discounts";
import { useAppTheme } from "../context/AppThemeContext";
import { useTenant } from "../context/TenantContext";
import AdminModalShell from "./AdminModalShell";
import DevActivityTracker from "./DevActivityTracker";
import ClassNXLoader from "./ClassNXLoader";
import { getCachedOrFetch, invalidateCache } from "../utils/requestCache";

const TENANTS_CACHE_KEY = "superadmin:tenants:viewAll";

const defaultPrefs = {
  showTenantPortfolio: true,
  showTenantEditor: true,
  showSubscriptionControls: true,
  showDiscountControls: true,
  showTrialRequests: true,
  showDevTracker: true,
};

const preferenceOptions = [
  { key: "showTenantPortfolio", label: "Tenant portfolio" },
  { key: "showTenantEditor", label: "Tenant editor" },
  { key: "showSubscriptionControls", label: "Subscription controls" },
  { key: "showDiscountControls", label: "Discount controls" },
  { key: "showTrialRequests", label: "Trial extension requests" },
  { key: "showDevTracker", label: "Dev activity tracker" },
];

const loadPrefs = () => {
  try {
    const stored = localStorage.getItem("superAdminDashboardPrefs");
    if (!stored) return defaultPrefs;
    const parsed = JSON.parse(stored);
    return { ...defaultPrefs, ...parsed };
  } catch {
    return defaultPrefs;
  }
};

const extractTenants = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tenants)) return payload.tenants;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const SUPPORTED_PLAN_OPTIONS = ["FREE", "PREMIUM"];
const ALLOWED_COUPON_PERCENTS = new Set([25, 50]);
const REFERRAL_FIXED_PERCENT = 5;
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,19}$/;

const normalizeOfferCode = (value = "") => String(value)
  .toUpperCase()
  .replace(/[^A-Z0-9_-]/g, "")
  .slice(0, 20);

const normalizePlan = (value = "") => {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) return "";
  if (["FREE", "STARTER", "BASIC"].includes(normalized)) return "FREE";
  if (["PREMIUM", "GROWTH", "ENTERPRISE", "PRO"].includes(normalized)) return "PREMIUM";
  return normalized;
};

const normalizeTenant = (record = {}, index = 0) => ({
  id: record.id || record.tenantId || record._id || `tenant-${index + 1}`,
  name: record.name || record.schoolName || record.organizationName || record.tenantName || `Tenant ${index + 1}`,
  adminName: record.adminName || record.name || "",
  schoolName: record.schoolName || record.name || "",
  adminEmail: record.adminEmail || record.email || "",
  adminMobile: record.adminMobile || record.mobile || record.phone || "",
  country: record.country || "",
  plan: normalizePlan(record.plan || record.subscriptionPlan || record.tier || "") || "FREE",
  campuses: Number(record.campuses ?? 1),
  admins: Number(record.admins ?? record.adminCount ?? 0),
  students: Number(record.students ?? record.studentCount ?? 0),
  status: String(record.status || "ACTIVE").toUpperCase(),
  // `planExpiryDate` is the authoritative subscription expiry from Tenant.
  planExpiryDate: record.planExpiryDate || record.planExpiry || record.renewalDate || record.expiresOn || "",
  planExpired: record.planExpired === true,
  lastReminderSentAt: record.lastReminderSentAt || "",
  renewalDate: record.renewalDate || record.planExpiryDate || record.planExpiry || record.expiresOn || "",
});

const formatPlanExpiryDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

const formatReminderDate = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const formatMoney = (value, currency = "INR") => {
  const amount = Number(value || 0);
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount); }
  catch { return `${currency} ${amount.toLocaleString("en-IN")}`; }
};

const toUniqueList = (values = []) => [...new Set(values.filter(Boolean))];

const pickValueFromObject = (item, keys = []) => {
  if (!item || typeof item !== "object") return "";
  const firstKey = keys.find((key) => item[key] !== undefined && item[key] !== null && String(item[key]).trim());
  return firstKey ? String(item[firstKey]).trim() : "";
};

const extractDropdownOptionsFromViewAllTenants = (responseData) => {
  const tenants = extractTenants(responseData);

  let plans = toUniqueList(
    tenants
      .map((record) => normalizePlan(pickValueFromObject(record, ["plan", "tier", "subscriptionPlan"])))
      .filter((value) => SUPPORTED_PLAN_OPTIONS.includes(value))
      .filter(Boolean)
  );

  if (plans.length === 0) {
    plans = [...SUPPORTED_PLAN_OPTIONS];
  }

  const statuses = toUniqueList(
    tenants
      .map((record) => pickValueFromObject(record, ["status"]))
      .filter(Boolean)
      .map((value) => value.toUpperCase())
  );

  return { plans, statuses };
};

const emptyTenantForm = {
  id: "",
  adminName: "",
  schoolName: "",
  adminEmail: "",
  adminMobile: "",
  country: "",
  plan: "FREE",
  campuses: "1",
  admins: "0",
  students: "0",
  status: "",
};

const statusTone = (status, isDarkMode) => {
  if (status === "ACTIVE") {
    return isDarkMode
      ? "border-emerald-900/70 bg-emerald-950/40 text-emerald-200"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "TRIAL") {
    return isDarkMode
      ? "border-amber-900/70 bg-amber-950/40 text-amber-200"
      : "border-amber-200 bg-amber-50 text-amber-700";
  }
  return isDarkMode
    ? "border-rose-900/70 bg-rose-950/40 text-rose-200"
    : "border-rose-200 bg-rose-50 text-rose-700";
};

const SuperAdminSaaS = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const { activeTenant, registerTenants, setActiveTenantById } = useTenant();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTenant, setSavingTenant] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState("");
  const [deletingTenantId, setDeletingTenantId] = useState("");
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [isBulkDeletingTenants, setIsBulkDeletingTenants] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenantForm, setTenantForm] = useState(emptyTenantForm);
  const [subscriptionForm, setSubscriptionForm] = useState({
    plan: "FREE",
    renewalDate: "",
  });
  const [planOptions, setPlanOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [trialRequests, setTrialRequests] = useState([]);
  const [trialRequestsLoading, setTrialRequestsLoading] = useState(false);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [cancellationRequestsLoading, setCancellationRequestsLoading] = useState(false);
  const [approvingId, setApprovingId] = useState("");
  const [rejectingId, setRejectingId] = useState("");
  const [directCancellingTenantId, setDirectCancellingTenantId] = useState("");
  const [remindingTenantId, setRemindingTenantId] = useState("");
  const [financialSummaries, setFinancialSummaries] = useState([]);
  const [financialDetail, setFinancialDetail] = useState(null);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [draftPrefs, setDraftPrefs] = useState(loadPrefs);
  const [showCustomize, setShowCustomize] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountSaving, setDiscountSaving] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [referralPercent, setReferralPercent] = useState(5);
  const [couponCode, setCouponCode] = useState("");
  const [couponPercent, setCouponPercent] = useState("25");
  const [activePanel, setActivePanel] = useState(() => new URLSearchParams(window.location.search).get("panel") || "tenantPortfolio");

  const clearTenantList = () => {
    setTenants([]);
    registerTenants([]);
    setSelectedTenantId("");
  };

  const syncTenantList = (nextTenants) => {
    setTenants(nextTenants);
    registerTenants(nextTenants);
  };

  const fetchOverview = async ({ forceRefresh = false, withLoader = true } = {}) => {
    if (withLoader) {
      setLoading(true);
    }

    clearTenantList();
    setPlanOptions([]);
    setStatusOptions([]);

    try {
      const viewAllTenantsResponse = await getCachedOrFetch(
        TENANTS_CACHE_KEY,
        () => api.get(API_PATHS.superAdmin.viewAllTenants()),
        { ttlMs: 120000, forceRefresh }
      );

      const tenantsFromViewAll = extractTenants(viewAllTenantsResponse?.data);
      const nextTenants = tenantsFromViewAll.map(normalizeTenant);
      const { plans: nextPlanOptions, statuses: nextStatusOptions } =
        extractDropdownOptionsFromViewAllTenants(viewAllTenantsResponse?.data);

      setPlanOptions(nextPlanOptions);
      setStatusOptions(nextStatusOptions);

      if (nextTenants.length > 0) {
        syncTenantList(nextTenants);
      } else {
        clearTenantList();
      }
      return true;
    } catch {
      clearTenantList();
      setPlanOptions([]);
      setStatusOptions([]);
      toast.error("Unable to load tenant data from backend.");
      return false;
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  };

  const fetchTrialRequests = async ({ withLoader = true } = {}) => {
    if (withLoader) setTrialRequestsLoading(true);
    try {
      const resp = await api.get(API_PATHS.superAdmin.trialRequests());
      const data = resp?.data?.data ?? resp?.data ?? [];
      setTrialRequests(Array.isArray(data) ? data : []);
    } catch {
      setTrialRequests([]);
    } finally {
      if (withLoader) setTrialRequestsLoading(false);
    }
  };

  const fetchCancellationRequests = async ({ withLoader = true } = {}) => {
    if (withLoader) setCancellationRequestsLoading(true);
    try {
      const data = await getCancellationRequests();
      setCancellationRequests(data);
    } catch {
      setCancellationRequests([]);
    } finally {
      if (withLoader) setCancellationRequestsLoading(false);
    }
  };

  const fetchDiscounts = async () => {
    setDiscountLoading(true);
    try {
      const config = await getDiscountConfig();
      setCoupons(Array.isArray(config?.coupons) ? config.coupons : []);
      setReferralPercent(REFERRAL_FIXED_PERCENT);
    } catch {
      setCoupons([]);
      setReferralPercent(REFERRAL_FIXED_PERCENT);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleApproveTrialRequest = async (request) => {
    setApprovingId(request.id);
    try {
      await api.post(API_PATHS.superAdmin.approveTrialRequest(request.id), {});
      setTrialRequests((prev) => prev.filter((r) => r.id !== request.id));
      invalidateCache(TENANTS_CACHE_KEY);
      await fetchOverview({ forceRefresh: true, withLoader: false });
      toast.success(`Trial extended for ${request.tenantName || request.id}.`);
    } catch (err) {
      toast.error(err?.message || "Failed to approve trial extension.");
    } finally {
      setApprovingId("");
    }
  };

  const handleRejectTrialRequest = async (request) => {
    setRejectingId(request.id);
    try {
      await api.post(API_PATHS.superAdmin.rejectTrialRequest(request.id), {});
      setTrialRequests((prev) => prev.filter((r) => r.id !== request.id));
      toast.info(`Trial request rejected for ${request.tenantName || request.id}.`);
    } catch (err) {
      toast.error(err?.message || "Failed to reject trial extension.");
    } finally {
      setRejectingId("");
    }
  };

  const handleExtendDirect = async (request, days = 7) => {
    setApprovingId(request.id);
    try {
      await api.post(API_PATHS.superAdmin.extendDirect(request.tenantId || request.id, days), {});
      setTrialRequests((prev) => prev.filter((r) => r.id !== request.id));
      invalidateCache(TENANTS_CACHE_KEY);
      await fetchOverview({ forceRefresh: true, withLoader: false });
      toast.success(`Subscription extended ${days} days for ${request.tenantName || request.id}.`);
    } catch (err) {
      toast.error(err?.message || "Failed to extend subscription.");
    } finally {
      setApprovingId("");
    }
  };

  const handleApproveCancellationRequest = async (request) => {
    const ok = window.confirm(`Approve cancellation request for ${request.tenantName || request.id}?`);
    if (!ok) return;
    setApprovingId(request.id);
    try {
      await approveCancellationRequest(request.id);
      setCancellationRequests((prev) => prev.filter((r) => r.id !== request.id));
      toast.success(`Cancellation approved for ${request.tenantName || request.id}.`);
      invalidateCache(TENANTS_CACHE_KEY);
      fetchOverview({ forceRefresh: true, withLoader: false });
    } catch (err) {
      toast.error(err?.message || "Failed to approve cancellation request.");
    } finally {
      setApprovingId("");
    }
  };

  const handleRejectCancellationRequest = async (request) => {
    const ok = window.confirm(`Reject cancellation request for ${request.tenantName || request.id}?`);
    if (!ok) return;
    setRejectingId(request.id);
    try {
      await rejectCancellationRequest(request.id);
      setCancellationRequests((prev) => prev.filter((r) => r.id !== request.id));
      toast.info(`Cancellation request rejected for ${request.tenantName || request.id}.`);
    } catch (err) {
      toast.error(err?.message || "Failed to reject cancellation request.");
    } finally {
      setRejectingId("");
    }
  };

  const handleCancelDirect = async () => {
    if (!selectedTenant?.id) {
      toast.error("Select a tenant first.");
      return;
    }
    const ok = window.confirm(`Cancel subscription directly for ${selectedTenant.name}?`);
    if (!ok) return;

    setDirectCancellingTenantId(selectedTenant.id);
    try {
      const tenantId = selectedTenant.id;
      await cancelSubscriptionDirect(selectedTenant.id);
      toast.success(`Subscription cancelled for ${selectedTenant.name}.`);
      invalidateCache(TENANTS_CACHE_KEY);
      await fetchOverview({ forceRefresh: true, withLoader: false });
      setSelectedTenantId(tenantId);
      await fetchCancellationRequests({ withLoader: false });
    } catch (err) {
      toast.error(err?.message || "Failed to cancel subscription directly.");
    } finally {
      setDirectCancellingTenantId("");
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchTrialRequests();
    fetchCancellationRequests();
    fetchDiscounts();
  }, []);

  const handleCreateCoupon = async (event) => {
    event.preventDefault();
    if (discountSaving) return;
    const safeCode = normalizeOfferCode(couponCode);
    const safePercent = Number(couponPercent || 0);
    if (!safeCode) {
      toast.error("Coupon code is required.");
      return;
    }
    if (!CODE_PATTERN.test(safeCode)) {
      toast.error("Coupon format invalid. Use 3-20 chars: A-Z, 0-9, _ or -.");
      return;
    }
    if (!ALLOWED_COUPON_PERCENTS.has(safePercent)) {
      toast.error("Only 25% and 50% coupons are allowed.");
      return;
    }

    setDiscountSaving(true);
    try {
      await createCoupon({ code: safeCode, discountPercent: safePercent });
      setCouponCode("");
      toast.success(`Coupon ${safeCode} saved.`);
      await fetchDiscounts();
    } catch (error) {
      toast.error(error?.message || "Could not save coupon.");
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleQuickCoupon = async (percent) => {
    if (!ALLOWED_COUPON_PERCENTS.has(Number(percent))) {
      toast.error("Only 25% and 50% quick coupons are allowed.");
      return;
    }
    if (discountSaving) return;
    const code = `SAVE${percent}`;
    setDiscountSaving(true);
    try {
      await createCoupon({ code, discountPercent: percent });
      toast.success(`Quick coupon ${code} enabled.`);
      await fetchDiscounts();
    } catch (error) {
      toast.error(error?.message || "Could not create quick coupon.");
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleToggleCoupon = async (coupon) => {
    if (discountSaving) return;
    const couponId = coupon?.id || coupon?.couponId;
    if (!couponId) {
      toast.error("Coupon id missing from backend response.");
      return;
    }
    setDiscountSaving(true);
    try {
      await updateCouponStatus({ couponId, active: !coupon?.active });
      toast.success(`Coupon ${coupon?.code || couponId} updated.`);
      await fetchDiscounts();
    } catch (error) {
      toast.error(error?.message || "Could not update coupon status.");
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleSaveReferral = async () => {
    if (discountSaving) return;
    const safePercent = Number(referralPercent || 0);
    if (safePercent !== REFERRAL_FIXED_PERCENT) {
      toast.error("Referral discount is locked to 5%.");
      return;
    }
    setDiscountSaving(true);
    try {
      await updateReferralPercent({ referralPercent: safePercent });
      toast.success("Referral discount updated.");
      await fetchDiscounts();
    } catch (error) {
      toast.error(error?.message || "Could not update referral discount.");
    } finally {
      setDiscountSaving(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("superAdminDashboardPrefs", JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    if (showCustomize) {
      setDraftPrefs(prefs);
    }
  }, [showCustomize, prefs]);

  const toggleDraftPref = (key) => {
    setDraftPrefs((current) => ({ ...current, [key]: !current[key] }));
  };

  const applyCustomization = () => {
    setPrefs(draftPrefs);
    setShowCustomize(false);
    toast.success("Super admin dashboard updated.");
  };

  const resetCustomization = () => {
    setDraftPrefs(defaultPrefs);
  };

  const handleRefreshTenants = async () => {
    const didRefresh = await fetchOverview({ forceRefresh: true, withLoader: true });
    if (didRefresh) {
      toast.success("Tenant records refreshed.");
    }
  };

  const handleSendPlanReminder = async (tenant) => {
    if (!tenant?.id || remindingTenantId) return;
    setRemindingTenantId(tenant.id);
    try {
      const response = await api.post(
        API_PATHS.superAdmin.tenantSubscriptionReminder(tenant.id),
        { reminderType: "PLAN_EXPIRY", channel: "EMAIL" }
      );
      const result = response?.data?.data ?? response?.data ?? {};
      toast.success(result.message || `Subscription reminder sent to ${result.sentTo || tenant.adminEmail || tenant.name}.`);
      invalidateCache(TENANTS_CACHE_KEY);
      await fetchOverview({ forceRefresh: true, withLoader: false });
      setSelectedTenantId(tenant.id);
    } catch (error) {
      toast.error(error?.message || "Unable to send the subscription reminder.");
    } finally {
      setRemindingTenantId("");
    }
  };

  const fetchFinancialSummaries = async () => {
    setFinancialsLoading(true);
    try {
      const response = await api.get(API_PATHS.superAdmin.subscriptionFinancials());
      const payload = response?.data?.data ?? response?.data ?? [];
      setFinancialSummaries(Array.isArray(payload) ? payload : payload?.summaries || payload?.content || []);
    } catch (error) {
      setFinancialSummaries([]);
      toast.error(error?.message || "Unable to load subscription financials.");
    } finally { setFinancialsLoading(false); }
  };

  const fetchTenantFinancials = async (tenantId) => {
    if (!tenantId) { setFinancialDetail(null); return; }
    setFinancialsLoading(true);
    try {
      const response = await api.get(API_PATHS.superAdmin.tenantSubscriptionFinancials(tenantId));
      setFinancialDetail(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      setFinancialDetail(null);
      toast.error(error?.message || "Unable to load tenant payment history.");
    } finally { setFinancialsLoading(false); }
  };

  useEffect(() => {
    if (activePanel !== "subscriptionFinancials") return;
    fetchFinancialSummaries();
  }, [activePanel]);

  useEffect(() => {
    if (activePanel !== "subscriptionFinancials") return;
    fetchTenantFinancials(selectedTenantId);
  }, [activePanel, selectedTenantId]);

  const financialTotals = useMemo(() => financialSummaries.reduce((totals, item) => ({
    grossRevenue: totals.grossRevenue + Number(item.grossRevenue || 0),
    netRevenue: totals.netRevenue + Number(item.netRevenue || 0),
    totalDiscountAmount: totals.totalDiscountAmount + Number(item.totalDiscountAmount || 0),
    retentionRevenue: totals.retentionRevenue + Number(item.retentionRevenue || 0),
  }), { grossRevenue: 0, netRevenue: 0, totalDiscountAmount: 0, retentionRevenue: 0 }), [financialSummaries]);

  const summary = useMemo(
    () => ({
      tenants: tenants.length,
      students: tenants.reduce((sum, item) => sum + item.students, 0),
      admins: tenants.reduce((sum, item) => sum + item.admins, 0),
      campuses: tenants.reduce((sum, item) => sum + item.campuses, 0),
    }),
    [tenants]
  );

  const selectedTenant = useMemo(() => {
    if (!tenants.length || !selectedTenantId) return null;
    return tenants.find((item) => item.id === selectedTenantId) || null;
  }, [selectedTenantId, tenants]);

  const tenantPlanOptions = useMemo(
    () => {
      const fromApi = toUniqueList((planOptions || []).map((item) => normalizePlan(item)).filter((item) => SUPPORTED_PLAN_OPTIONS.includes(item)));
      return toUniqueList([...SUPPORTED_PLAN_OPTIONS, ...fromApi]);
    },
    [planOptions]
  );

  const tenantStatusOptions = useMemo(
    () => toUniqueList(statusOptions || []),
    [statusOptions]
  );

  const subscriptionPlanOptions = useMemo(
    () => tenantPlanOptions,
    [tenantPlanOptions]
  );

  useEffect(() => {
    if (!selectedTenant) {
      setTenantForm(emptyTenantForm);
      setSubscriptionForm({ plan: "FREE", renewalDate: "" });
      return;
    }

    setTenantForm({
      id: selectedTenant.id,
      name: selectedTenant.name,
      schoolName: selectedTenant.schoolName || "",
      plan: selectedTenant.plan,
      campuses: String(selectedTenant.campuses),
      admins: String(selectedTenant.admins),
      students: String(selectedTenant.students),
      status: selectedTenant.status,
    });

    setSubscriptionForm({
      plan: selectedTenant.plan,
      renewalDate: selectedTenant.renewalDate,
    });
  }, [selectedTenant]);

  const updateTenantForm = (key, value) => {
    setTenantForm((current) => ({ ...current, [key]: value }));
  };

  const updateSubscriptionForm = (key, value) => {
    setSubscriptionForm((current) => ({ ...current, [key]: value }));
  };

  const selectTenant = (tenantId) => {
    setSelectedTenantId(tenantId);
    setActiveTenantById(tenantId);
  };

  const toggleTenantSelection = (tenantId) => {
    const isSelected = selectedTenantIds.includes(tenantId);
    const nextSelectedIds = isSelected
      ? selectedTenantIds.filter((id) => id !== tenantId)
      : [...selectedTenantIds, tenantId];
    setSelectedTenantIds(nextSelectedIds);

    if (!isSelected) {
      selectTenant(tenantId);
    } else if (selectedTenantId === tenantId) {
      const nextActiveTenantId = nextSelectedIds[nextSelectedIds.length - 1] || "";
      if (nextActiveTenantId) selectTenant(nextActiveTenantId);
      else setSelectedTenantId("");
    }
  };

  const toggleAllTenantSelections = () => {
    const allSelected = tenants.length > 0 && tenants.every((tenant) => selectedTenantIds.includes(tenant.id));
    const nextSelectedIds = allSelected ? [] : tenants.map((tenant) => tenant.id);
    setSelectedTenantIds(nextSelectedIds);
    if (nextSelectedIds.length > 0 && !nextSelectedIds.includes(selectedTenantId)) {
      selectTenant(nextSelectedIds[0]);
    } else if (nextSelectedIds.length === 0 && tenants.some((tenant) => tenant.id === selectedTenantId)) {
      setSelectedTenantId("");
    }
  };

  const upsertTenant = (nextTenant) => {
    const nextTenants = (() => {
      const exists = tenants.some((item) => item.id === nextTenant.id);
      if (!exists) return [nextTenant, ...tenants];
      return tenants.map((item) => (item.id === nextTenant.id ? nextTenant : item));
    })();

    syncTenantList(nextTenants);
    selectTenant(nextTenant.id);
  };

  const handleCreateTenant = () => {
    navigate("/super-admin/tenants/new");
  };

  const handleOpenTenantEdit = () => {
    if (!selectedTenant) {
      toast.error("Select a tenant first.");
      return;
    }
    navigate(`/super-admin/tenants/${encodeURIComponent(selectedTenant.id)}/edit`);
  };

  const handleOpenSubscriptionEditor = () => {
    const tenant = selectedTenant || tenants[0];
    if (!tenant) {
      toast.error("No tenants are available.");
      return;
    }
    selectTenant(tenant.id);
    setSubscriptionForm({
      plan: tenant.plan,
      renewalDate: tenant.renewalDate,
    });
    setIsSubscriptionModalOpen(true);
  };

  const handleSaveTenant = async (event) => {
    event.preventDefault();

    const payload = {
      adminName: tenantForm.adminName?.trim() || "",
      schoolName: tenantForm.schoolName?.trim() || "",
      adminEmail: tenantForm.adminEmail?.trim() || "",
      adminMobile: tenantForm.adminMobile?.trim() || "",
      country: tenantForm.country?.trim() || "",
      plan: tenantForm.plan,
      campuses: Number(tenantForm.campuses || 0),
      admins: Number(tenantForm.admins || 0),
      students: Number(tenantForm.students || 0),
      status: tenantForm.status,
    };

    if (!payload.adminName) {
      toast.error("Tenant name is required.");
      return;
    }

    const isEditing = Boolean(tenantForm.id);
    setSavingTenant(true);

    try {
      const response = isEditing
        ? await updateTenant(tenantForm.id, payload)
        : await createTenant(payload);
      invalidateCache(TENANTS_CACHE_KEY);

      const responseData = response?.data?.data ?? response?.data ?? response ?? {};
      const nextTenant = normalizeTenant(
        {
          ...responseData,
          ...payload,
          id: isEditing ? tenantForm.id : undefined,
        },
        tenants.length
      );

      upsertTenant(nextTenant);
      setIsTenantModalOpen(false);
      toast.success(isEditing ? "Tenant updated." : "Tenant created successfully.");
    } catch (error) {
      toast.error(error?.message || "Unable to save tenant.");
    } finally {
      setSavingTenant(false);
    }
  };

  const handleToggleStatus = async (tenant) => {
    const nextStatus = tenant.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    setUpdatingStatusId(tenant.id);

    try {
      await api.patch(API_PATHS.superAdmin.tenantStatus(tenant.id), {
        status: nextStatus,
      });
      invalidateCache(TENANTS_CACHE_KEY);

      upsertTenant({ ...tenant, status: nextStatus });
      toast.success(`Tenant status changed to ${nextStatus}.`);
    } catch (error) {
      toast.error(error?.message || "Unable to update tenant status.");
    } finally {
      setUpdatingStatusId("");
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!window.confirm(`Permanently delete ${tenant.name}? This action cannot be undone.`)) return;
    setDeletingTenantId(tenant.id);

    try {
      await deleteTenant(tenant.id);
      const remainingTenants = tenants.filter((item) => item.id !== tenant.id);
      invalidateCache(TENANTS_CACHE_KEY);
      syncTenantList(remainingTenants);
      setSelectedTenantIds((current) => current.filter((id) => id !== tenant.id));
      if (selectedTenantId === tenant.id) {
        setSelectedTenantId(remainingTenants[0]?.id || "");
      }
      toast.success("Tenant deleted successfully.");
    } catch (error) {
      toast.error(error?.message || "Unable to delete tenant.");
    } finally {
      setDeletingTenantId("");
    }
  };

  const handleDeleteSelectedTenants = async () => {
    const selectedTenants = tenants.filter((tenant) => selectedTenantIds.includes(tenant.id));
    if (selectedTenants.length === 0) return;
    if (!window.confirm(`Permanently delete ${selectedTenants.length} selected tenants? This action cannot be undone.`)) return;

    setIsBulkDeletingTenants(true);
    try {
      const results = await Promise.allSettled(selectedTenants.map((tenant) => deleteTenant(tenant.id)));
      const deletedIds = selectedTenants
        .filter((tenant, index) => results[index].status === "fulfilled")
        .map((tenant) => tenant.id);
      const failedCount = selectedTenants.length - deletedIds.length;

      if (deletedIds.length > 0) {
        const remainingTenants = tenants.filter((tenant) => !deletedIds.includes(tenant.id));
        invalidateCache(TENANTS_CACHE_KEY);
        syncTenantList(remainingTenants);
        setSelectedTenantIds((current) => current.filter((id) => !deletedIds.includes(id)));
        if (deletedIds.includes(selectedTenantId)) {
          setSelectedTenantId(remainingTenants[0]?.id || "");
        }
      }

      if (failedCount === 0) toast.success(`${deletedIds.length} tenants deleted successfully.`);
      else toast.error(`${deletedIds.length} deleted; ${failedCount} failed. Failed tenants remain selected.`);
    } finally {
      setIsBulkDeletingTenants(false);
    }
  };

  const handleSaveSubscription = async (event) => {
    event.preventDefault();

    if (!selectedTenant) {
      toast.error("Select a tenant first.");
      return;
    }

    setSavingSubscription(true);
    const payload = {
      tenantId: selectedTenant.id,
      plan: subscriptionForm.plan,
      renewalDate: subscriptionForm.renewalDate,
    };

    if (!payload.plan) {
      toast.error("Plan must be selected from backend dropdown data.");
      setSavingSubscription(false);
      return;
    }

    try {
      await api.patch(API_PATHS.superAdmin.tenantSubscription(selectedTenant.id), payload);
      invalidateCache(TENANTS_CACHE_KEY);
      await fetchOverview({ forceRefresh: true, withLoader: false });
      setSelectedTenantId(payload.tenantId);
      setIsSubscriptionModalOpen(false);
      toast.success("Subscription updated.");
    } catch (error) {
      toast.error(error?.message || "Unable to update subscription.");
    } finally {
      setSavingSubscription(false);
    }
  };

  const panelClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-black/20"
    : "border-slate-200 bg-white shadow-slate-200/60";

  const sidePanelItems = useMemo(
    () => [
      {
        id: "tenantPortfolio",
        title: "Tenants",
        description: "List, create, and update tenants",
        icon: FaBuilding,
        enabled: prefs.showTenantPortfolio,
      },
      {
        id: "subscriptionControls",
        title: "Subscriptions",
        description: "Plan and renewal",
        icon: FaMoneyBillWave,
        enabled: prefs.showSubscriptionControls,
      },
      {
        id: "subscriptionFinancials",
        title: "Financials",
        description: "Revenue and payment history",
        icon: FaChartLine,
        enabled: prefs.showSubscriptionControls,
      },
      {
        id: "discountControls",
        title: "Discount Controls",
        description: "Coupons and referral",
        icon: FaTag,
        enabled: prefs.showDiscountControls,
      },
      {
        id: "trialRequests",
        title: "Trial Requests",
        description: "Pending extension approvals",
        icon: FaHourglassHalf,
        count: trialRequests.length,
        enabled: prefs.showTrialRequests,
      },
      {
        id: "cancellationRequests",
        title: "Cancel Requests",
        description: "Pending cancellation approvals",
        icon: FaPowerOff,
        count: cancellationRequests.length,
        enabled: prefs.showTrialRequests,
      },
      {
        id: "devActivityTracker",
        title: "Dev Activity Tracker",
        description: "Track development tasks and bugs",
        icon: FaTasks,
        enabled: prefs.showDevTracker,
      },
    ],
    [
      cancellationRequests.length,
      prefs.showDevTracker,
      prefs.showDiscountControls,
      prefs.showSubscriptionControls,
      prefs.showTenantEditor,
      prefs.showTenantPortfolio,
      prefs.showTrialRequests,
      trialRequests.length,
    ]
  );

  useEffect(() => {
    const isCurrentVisible = sidePanelItems.some((item) => item.id === activePanel && item.enabled);
    if (isCurrentVisible) return;

    const firstVisible = sidePanelItems.find((item) => item.enabled);
    if (firstVisible?.id) {
      setActivePanel(firstVisible.id);
    }
  }, [activePanel, sidePanelItems]);

  useEffect(() => {
    const handleSidebarAction = (event) => {
      const action = event?.detail?.action;
      if (!action) return;
      setActivePanel(action);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("super-admin:select-action", handleSidebarAction);
    return () => window.removeEventListener("super-admin:select-action", handleSidebarAction);
  }, []);

  return (
    <div
      className={`min-h-screen px-3 py-4 md:px-6 md:py-8 ${isDarkMode
          ? "bg-slate-950 text-slate-100"
          : "bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.10),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#ffffff_45%,_#faf5ff_100%)] text-slate-900"
        }`}
    >
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="space-y-6">
          <div className="space-y-6">
            {(activePanel === "tenantPortfolio" || activePanel === "subscriptionControls" || activePanel === "subscriptionFinancials") && (
              <div className={`overflow-x-auto border-b px-4 pt-3 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex min-w-max gap-2" role="tablist" aria-label="Tenant management">
                  <button type="button" role="tab" aria-selected={activePanel === "tenantPortfolio"} onClick={() => setActivePanel("tenantPortfolio")} className={`inline-flex items-center gap-2 rounded-t-md border px-5 py-3 text-sm font-semibold transition-all duration-200 ${activePanel === "tenantPortfolio" ? isDarkMode ? "border-sky-700 bg-sky-950/40 text-sky-300" : "border-sky-300 bg-sky-50 text-sky-700 shadow-[0_0_0_1px_rgba(14,165,233,0.15)]" : isDarkMode ? "border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}><FaBuilding /> Tenant directory</button>
                  <button type="button" role="tab" aria-selected={activePanel === "subscriptionControls"} onClick={() => setActivePanel("subscriptionControls")} className={`inline-flex items-center gap-2 rounded-t-md border px-5 py-3 text-sm font-semibold transition-all duration-200 ${activePanel === "subscriptionControls" ? isDarkMode ? "border-sky-700 bg-sky-950/40 text-sky-300" : "border-sky-300 bg-sky-50 text-sky-700 shadow-[0_0_0_1px_rgba(14,165,233,0.15)]" : isDarkMode ? "border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}><FaMoneyBillWave /> Subscriptions</button>
                  <button type="button" role="tab" aria-selected={activePanel === "subscriptionFinancials"} onClick={() => setActivePanel("subscriptionFinancials")} className={`inline-flex items-center gap-2 rounded-t-md border px-5 py-3 text-sm font-semibold transition-all duration-200 ${activePanel === "subscriptionFinancials" ? isDarkMode ? "border-sky-700 bg-sky-950/40 text-sky-300" : "border-sky-300 bg-sky-50 text-sky-700 shadow-[0_0_0_1px_rgba(14,165,233,0.15)]" : isDarkMode ? "border-transparent text-slate-400 hover:bg-slate-800 hover:text-slate-200" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}><FaChartLine /> Financials</button>
                </div>
              </div>
            )}
            {prefs.showTenantPortfolio && activePanel === "tenantPortfolio" && (
              <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Tenants
                    </p>
                    <h2 className="mt-2 text-2xl font-bold">Tenant status and usage</h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDeleteSelectedTenants}
                      disabled={selectedTenantIds.length === 0 || isBulkDeletingTenants || Boolean(deletingTenantId)}
                      className="inline-flex items-center gap-2 rounded-full bg-rose-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <FaTrash /> {isBulkDeletingTenants ? "Deleting..." : `Delete selected (${selectedTenantIds.length})`}
                    </button>
                    <button
                      type="button"
                      onClick={handleRefreshTenants}
                      disabled={loading}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${isDarkMode
                          ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
                          : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                        }`}
                    >
                      <FaSync className={loading ? "animate-spin" : ""} />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateTenant}
                      className="rounded-full bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-fuchsia-700"
                    >
                      Add tenant
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {loading ? (
                    <ClassNXLoader label="Loading tenants" detail="Preparing your portfolio." />
                  ) : tenants.length === 0 ? (
                    <div
                      className={`rounded-2xl border px-4 py-10 text-center text-sm ${isDarkMode
                          ? "border-slate-800 bg-slate-950 text-slate-400"
                          : "border-slate-200 bg-slate-50 text-slate-500"
                        }`}
                    >
                      No tenants found from backend.
                    </div>
                  ) : (
                    <div className={`overflow-x-auto rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-100"}`}>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                              <input
                                type="checkbox"
                                aria-label="Select all tenants"
                                checked={tenants.length > 0 && tenants.every((tenant) => selectedTenantIds.includes(tenant.id))}
                                onChange={toggleAllTenantSelections}
                                disabled={isBulkDeletingTenants}
                                className="h-4 w-4 cursor-pointer accent-sky-600"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Tenant Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Subdomain</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Plan</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Status</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Campuses</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Admins</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Students</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Expiry date</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Plan expired</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tenants.map((tenant) => (
                            <tr
                              key={tenant.id}
                              className={`border-b transition ${selectedTenantIds.includes(tenant.id) || selectedTenant?.id === tenant.id
                                ? isDarkMode
                                  ? "border-fuchsia-500/50 bg-fuchsia-950/20"
                                  : "border-fuchsia-200 bg-fuchsia-50"
                                : isDarkMode
                                  ? "border-slate-800 hover:bg-slate-900"
                                  : "border-slate-200 hover:bg-slate-100"
                              }`}
                            >
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${tenant.name}`}
                                  checked={selectedTenantIds.includes(tenant.id)}
                                  onChange={() => toggleTenantSelection(tenant.id)}
                                  disabled={isBulkDeletingTenants}
                                  className="h-4 w-4 cursor-pointer"
                                />
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold">{tenant.name}</td>
                              <td className="px-4 py-3 text-sm font-mono">
                                {tenant.subdomain ? (
                                  <span className="inline-flex items-center rounded-md bg-sky-500/10 px-2 py-0.5 text-xs font-semibold text-sky-400 border border-sky-500/20">
                                    {tenant.subdomain.includes(".") ? tenant.subdomain : `${tenant.subdomain}.classnx.com`}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-500">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm">{tenant.plan}</td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                                    statusTone(tenant.status, isDarkMode)
                                  }`}
                                  title={tenant.status}
                                  role="img"
                                  aria-label={`Status: ${tenant.status}`}
                                >
                                  {tenant.status === "ACTIVE" ? <FaCheckCircle /> : tenant.status === "TRIAL" ? <FaHourglassHalf /> : <FaTimesCircle />}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-sm font-semibold">{tenant.campuses}</td>
                              <td className="px-4 py-3 text-center text-sm font-semibold">{tenant.admins}</td>
                              <td className="px-4 py-3 text-center text-sm font-semibold">{tenant.students}</td>
                              <td className="px-4 py-3 text-sm text-slate-500">{formatPlanExpiryDate(tenant.planExpiryDate)}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm ${tenant.planExpired
                                  ? isDarkMode ? "border-rose-800 bg-rose-950/40 text-rose-300" : "border-rose-200 bg-rose-50 text-rose-700"
                                  : isDarkMode ? "border-emerald-800 bg-emerald-950/40 text-emerald-300" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                                }`} title={tenant.planExpired ? "Plan expired" : "Plan active"} role="img" aria-label={tenant.planExpired ? "Plan expired" : "Plan active"}>
                                  {tenant.planExpired ? <FaTimesCircle /> : <FaCheckCircle />}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      selectTenant(tenant.id);
                                      navigate(`/super-admin/tenants/${encodeURIComponent(tenant.id)}/edit`);
                                    }}
                                    aria-label={`Edit ${tenant.name}`}
                                    title={`Edit ${tenant.name}`}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${
                                      isDarkMode
                                        ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                                    }`}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleToggleStatus(tenant)}
                                    disabled={updatingStatusId === tenant.id}
                                    aria-label={`${tenant.status === "ACTIVE" ? "Suspend" : "Activate"} ${tenant.name}`}
                                    title={`${tenant.status === "ACTIVE" ? "Suspend" : "Activate"} ${tenant.name}`}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                                      tenant.status === "ACTIVE"
                                        ? "bg-rose-600 text-white hover:bg-rose-700"
                                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                                    } disabled:cursor-not-allowed disabled:opacity-60`}
                                  >
                                    {updatingStatusId === tenant.id ? <FaSync className="animate-spin" /> : tenant.status === "ACTIVE" ? <FaPause /> : <FaPlay />}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteTenant(tenant)}
                                    disabled={deletingTenantId === tenant.id || isBulkDeletingTenants}
                                    aria-label={`Delete ${tenant.name}`}
                                    title={`Delete ${tenant.name}`}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-700 text-white transition hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {deletingTenantId === tenant.id ? <FaSync className="animate-spin" /> : <FaTrash />}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {prefs.showSubscriptionControls && (activePanel === "subscriptionControls" || activePanel === "subscriptionFinancials") && (
              <div className={`overflow-hidden rounded-2xl border shadow-xl ${panelClass}`}>
                {activePanel === "subscriptionControls" && (
                <div className="border-b border-slate-800 bg-slate-950 px-6 py-7 text-white sm:px-8">
                  <div className="flex items-center justify-between gap-5">
                    <div><div className="mb-3 h-1 w-10 rounded-full bg-indigo-500" /><p className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-300">Billing administration</p><h2 className="mt-2 text-2xl font-bold sm:text-3xl">Subscription management</h2><p className="mt-2 max-w-2xl text-sm text-slate-400">Control tenant plans, billing periods, renewals, and account communication from one workspace.</p></div>
                    <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-xl text-indigo-300 sm:flex"><FaMoneyBillWave /></div>
                  </div>
                </div>
                )}
                <div className="p-6 sm:p-8">
                {activePanel === "subscriptionControls" && (<>
                <label className={`block text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                  <span className="flex items-center gap-2"><FaBuilding className="text-indigo-500" /> Tenant account</span>
                  <select
                    value={selectedTenantId}
                    onChange={(event) => selectTenant(event.target.value)}
                    className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm font-semibold shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900"}`}
                  >
                    <option value="">Choose a tenant</option>
                    {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                  </select>
                </label>
                <div className="mt-7 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
                {selectedTenant ? (
                  <div className={`overflow-hidden rounded-xl border ${isDarkMode ? "border-slate-700 bg-slate-950/70" : "border-slate-200 bg-white"}`}>
                    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-5 text-white">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-100">Current subscription</p>
                      <div className="mt-2 flex items-end justify-between gap-4"><p className="text-3xl font-black tracking-tight">{selectedTenant.plan}</p><span className="rounded-md border border-white/20 bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">{selectedTenant.name}</span></div>
                    </div>
                    <div className="grid divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0 divide-slate-200 dark:divide-slate-800">
                      <div className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Account status</p><span className={`mt-2 inline-flex rounded-md border px-2.5 py-1 text-xs font-bold ${statusTone(selectedTenant.status, isDarkMode)}`}>{selectedTenant.status}</span></div>
                      <div className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Expiry date</p><p className="mt-2 text-sm font-bold">{formatPlanExpiryDate(selectedTenant.planExpiryDate)}</p></div>
                      <div className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Billing state</p><p className={`mt-2 text-sm font-bold ${selectedTenant.planExpired ? "text-rose-500" : "text-emerald-500"}`}>{selectedTenant.planExpired ? "Expired" : "In good standing"}</p></div>
                    </div>
                  </div>
                ) : <div className={`rounded-xl border border-dashed px-5 py-12 text-center text-sm lg:col-span-2 ${isDarkMode ? "border-slate-700 text-slate-400" : "border-slate-300 text-slate-500"}`}><FaBuilding className="mx-auto mb-3 text-2xl text-slate-400" />Select a tenant to open its billing workspace.</div>}
                {selectedTenant && (
                  <div className={`relative overflow-hidden rounded-xl border p-5 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                    <div className="relative flex h-full flex-col justify-between gap-5">
                      <div className="flex min-w-0 items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600"><FaEnvelope /></div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2"><h3 className="font-bold">Subscription email reminder</h3><span className="rounded-full bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-500">15 min cooldown</span></div>
                          <p className={`mt-1 truncate text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Recipient: {selectedTenant.adminEmail || "No primary admin email"}</p>
                          <p className="mt-2 text-xs text-slate-500">Last sent: {formatReminderDate(selectedTenant.lastReminderSentAt)}</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => handleSendPlanReminder(selectedTenant)} disabled={remindingTenantId === selectedTenant.id || !selectedTenant.adminEmail} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-600 dark:hover:bg-indigo-500"><FaEnvelope />{remindingTenantId === selectedTenant.id ? "Sending reminder..." : "Send email reminder"}</button>
                    </div>
                  </div>
                )}
                </div>
                <div className={`mt-7 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-end ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                <button
                  type="button"
                  onClick={handleOpenSubscriptionEditor}
                  disabled={!selectedTenant}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-500/20 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaMoneyBillWave /> Manage subscription
                </button>
                <button
                  type="button"
                  onClick={handleCancelDirect}
                  disabled={!selectedTenant || directCancellingTenantId === selectedTenant?.id}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300 px-5 py-3 text-sm font-semibold text-rose-600 transition hover:border-rose-400 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaPowerOff />
                  {directCancellingTenantId === selectedTenant?.id ? "Cancelling…" : "Cancel subscription directly"}
                </button>
                </div>
                </>)}

                {activePanel === "subscriptionFinancials" && (<section>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-500">Revenue intelligence</p><h3 className="mt-2 text-xl font-bold">Subscription financials</h3><p className="mt-1 text-sm text-slate-500">Successful collections, discounts, and recurring revenue across tenants.</p></div>
                    <button type="button" onClick={() => { fetchFinancialSummaries(); fetchTenantFinancials(selectedTenantId); }} disabled={financialsLoading} className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold ${isDarkMode ? "border-slate-700 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-50"}`}><FaSync className={financialsLoading ? "animate-spin" : ""} /> Refresh financials</button>
                  </div>
                  <label className={`mt-6 block max-w-xl text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Tenant payment history<select value={selectedTenantId} onChange={(event) => selectTenant(event.target.value)} className={`mt-2 w-full rounded-lg border px-4 py-3 text-sm font-semibold outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white"}`}><option value="">Choose a tenant</option>{tenants.map((tenant) => <option key={`financial-tenant-${tenant.id}`} value={tenant.id}>{tenant.name}</option>)}</select></label>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      ["Gross revenue", financialTotals.grossRevenue, "Before discounts"],
                      ["Net revenue", financialTotals.netRevenue, "Successful collections"],
                      ["Total discounts", financialTotals.totalDiscountAmount, "Coupons and referrals"],
                      ["Retention revenue", financialTotals.retentionRevenue, "Successful renewals"],
                    ].map(([label, value, note], index) => <div key={label} className={`rounded-xl border p-4 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-white"}`}><div className="flex items-center justify-between"><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>{index === 1 ? <FaChartLine className="text-emerald-500" /> : <FaCreditCard className="text-indigo-400" />}</div><p className="mt-3 text-xl font-black">{formatMoney(value, financialSummaries[0]?.currency || "INR")}</p><p className="mt-1 text-xs text-slate-500">{note}</p></div>)}
                  </div>

                  {selectedTenant && financialDetail?.summary && (
                    <div className={`mt-6 rounded-xl border p-5 ${isDarkMode ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-xs font-bold uppercase tracking-wider text-indigo-500">Selected tenant</p><h4 className="mt-1 text-lg font-bold">{financialDetail.summary.schoolName || selectedTenant.name}</h4></div><span className="rounded-md bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-600">{financialDetail.summary.successfulPayments || 0} successful payments</span></div>
                      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {[["Net revenue", formatMoney(financialDetail.summary.netRevenue, financialDetail.summary.currency)], ["Renewals", financialDetail.summary.renewalCount || 0], ["Average payment", formatMoney(financialDetail.summary.averagePaymentValue, financialDetail.summary.currency)], ["Annual value", formatMoney(financialDetail.summary.projectedAnnualValue, financialDetail.summary.currency)]].map(([label, value]) => <div key={label}><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-bold">{value}</p></div>)}
                      </div>
                    </div>
                  )}

                  {selectedTenant && (
                    <div className={`mt-6 overflow-hidden rounded-xl border ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
                      <div className={`flex items-center justify-between px-5 py-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}><div><h4 className="font-bold">Payment history</h4><p className="mt-1 text-xs text-slate-500">Complete subscription transaction history</p></div><span className="text-xs font-semibold text-slate-500">{financialDetail?.subscriptions?.length || 0} records</span></div>
                      <div className="overflow-x-auto"><table className="w-full min-w-[850px] text-left"><thead className={isDarkMode ? "bg-slate-900" : "bg-white"}><tr>{["Date", "Plan", "Status", "Gross", "Discount", "Charged", "Coupon", "Payment ID"].map((heading) => <th key={heading} className="border-t border-inherit px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">{heading}</th>)}</tr></thead><tbody>{financialsLoading ? <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500">Loading payment history...</td></tr> : !financialDetail?.subscriptions?.length ? <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-slate-500">No subscription payments found.</td></tr> : financialDetail.subscriptions.map((payment) => <tr key={payment.paymentRecordId || payment.providerOrderId} className="border-t border-inherit"><td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{formatReminderDate(payment.createdAt)}</td><td className="px-4 py-3 text-sm font-semibold">{payment.plan}</td><td className="px-4 py-3"><span className={`rounded-md px-2 py-1 text-[10px] font-bold ${payment.successful ? "bg-emerald-100 text-emerald-700" : payment.status === "PENDING" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{payment.status}</span></td><td className="px-4 py-3 text-sm">{formatMoney(payment.grossAmount, payment.currency)}</td><td className="px-4 py-3 text-sm text-rose-500">-{formatMoney(payment.totalDiscountAmount, payment.currency)}</td><td className="px-4 py-3 text-sm font-bold">{formatMoney(payment.chargedAmount, payment.currency)}</td><td className="px-4 py-3 text-xs text-slate-500">{payment.couponCode || "—"}</td><td className="max-w-[160px] truncate px-4 py-3 text-xs text-slate-500" title={payment.providerPaymentId}>{payment.providerPaymentId || "—"}</td></tr>)}</tbody></table></div>
                    </div>
                  )}
                  <p className="mt-4 text-xs leading-5 text-slate-500">Revenue reporting only. Taxes, refunds, gateway fees, chargebacks, and operating costs are not included, so these figures do not represent accounting profit.</p>
                </section>)}
                </div>
              </div>
            )}

            {prefs.showDiscountControls && activePanel === "discountControls" && (
              <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Discount controls
                    </p>
                    <h2 className="mt-2 text-2xl font-bold">Coupons and referral rules</h2>
                  </div>
                  <button
                    type="button"
                    onClick={fetchDiscounts}
                    disabled={discountLoading || discountSaving}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${isDarkMode
                        ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                  >
                    <FaSync className={discountLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>
                </div>

                <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  Configure discount benefits: coupon offers (like 25% and 50%) and referral reward (default 5%).
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleQuickCoupon(25)}
                    disabled={discountSaving}
                    className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                  >
                    Enable SAVE25
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickCoupon(50)}
                    disabled={discountSaving}
                    className="rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
                  >
                    Enable SAVE50
                  </button>
                </div>

                <form onSubmit={handleCreateCoupon} className="mt-4 grid gap-3 sm:grid-cols-[1.3fr_0.9fr_auto]">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(event) => setCouponCode(normalizeOfferCode(event.target.value))}
                    placeholder="Coupon code"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm uppercase tracking-wide focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                  />
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={couponPercent}
                    onChange={(event) => setCouponPercent(event.target.value)}
                    placeholder="Discount %"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                  />
                  <button
                    type="submit"
                    disabled={discountSaving}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-fuchsia-700 disabled:opacity-60"
                  >
                    <FaTag /> Save coupon
                  </button>
                </form>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Referral discount</p>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      min="5"
                      max="5"
                      value={referralPercent}
                      onChange={() => setReferralPercent(REFERRAL_FIXED_PERCENT)}
                      className="w-28 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                    />
                    <button
                      type="button"
                      onClick={handleSaveReferral}
                      disabled={discountSaving}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                    >
                      Save referral %
                    </button>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Active coupons</p>
                  {discountLoading ? (
                    <p className="text-sm text-slate-500">Loading coupons...</p>
                  ) : coupons.length === 0 ? (
                    <p className="text-sm text-slate-500">No coupons configured yet.</p>
                  ) : (
                    coupons.map((coupon) => (
                      <div
                        key={coupon.id || coupon.couponId || coupon.code}
                        className={`flex items-center justify-between rounded-xl border px-3 py-2 text-sm ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
                          }`}
                      >
                        <div>
                          <p className="font-semibold">{coupon.code || "COUPON"}</p>
                          <p className="text-xs text-slate-500">
                            {Number(coupon.discountPercent ?? coupon.percent ?? 0) || 0}% off
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleCoupon(coupon)}
                          disabled={discountSaving}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold text-white transition disabled:opacity-60 ${coupon.active === false ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
                            }`}
                        >
                          {coupon.active === false ? "Activate" : "Disable"}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {prefs.showTrialRequests && activePanel === "trialRequests" && (
              <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Trial extension requests
                    </p>
                    <h2 className="mt-2 text-2xl font-bold">Pending requests</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => fetchTrialRequests()}
                    disabled={trialRequestsLoading}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${isDarkMode
                        ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                  >
                    <FaSync className={trialRequestsLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>
                </div>

                {trialRequestsLoading ? (
                  <div className={`rounded-2xl border px-4 py-8 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}>
                    Loading requests…
                  </div>
                ) : trialRequests.length === 0 ? (
                  <div className={`rounded-2xl border px-4 py-8 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}>
                    <FaHourglassHalf className="mx-auto mb-2 text-2xl opacity-40" />
                    <p>No pending trial extension requests.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {trialRequests.map((req) => (
                      <div
                        key={req.id}
                        className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                          }`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-semibold text-sm">
                              {req.tenantName || req.schoolName || `Tenant ${req.tenantId || req.id}`}
                            </p>
                            <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                              Requested by: {req.requestedBy || req.adminEmail || req.tenantId || "—"}
                            </p>
                            {req.requestedAt && (
                              <p className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                {new Date(req.requestedAt).toLocaleString("en-IN", {
                                  day: "numeric", month: "short", year: "numeric",
                                  hour: "2-digit", minute: "2-digit",
                                })}
                              </p>
                            )}
                            {(req.message || req.reason) && (
                              <p className={`mt-1 text-xs italic line-clamp-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                "{req.message || req.reason}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleApproveTrialRequest(req)}
                              disabled={approvingId === req.id || rejectingId === req.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaCheckCircle />
                              {approvingId === req.id ? "Processing…" : "Approve"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleExtendDirect(req, 7)}
                              disabled={approvingId === req.id || rejectingId === req.id}
                              title="Directly extend subscription 7 days without waiting for approval"
                              className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              +7d
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRejectTrialRequest(req)}
                              disabled={approvingId === req.id || rejectingId === req.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaTimesCircle />
                              {rejectingId === req.id ? "Rejecting…" : "Reject"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {prefs.showTrialRequests && activePanel === "cancellationRequests" && (
              <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Subscription cancellation requests
                    </p>
                    <h2 className="mt-2 text-2xl font-bold">Pending cancellation approvals</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => fetchCancellationRequests()}
                    disabled={cancellationRequestsLoading}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:opacity-60 ${isDarkMode
                        ? "border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                  >
                    <FaSync className={cancellationRequestsLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>
                </div>

                {cancellationRequestsLoading ? (
                  <div className={`rounded-2xl border px-4 py-8 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}>
                    Loading cancellation requests…
                  </div>
                ) : cancellationRequests.length === 0 ? (
                  <div className={`rounded-2xl border px-4 py-8 text-center text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}>
                    <FaHourglassHalf className="mx-auto mb-2 text-2xl opacity-40" />
                    <p>No pending cancellation requests.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cancellationRequests.map((req) => (
                      <div
                        key={req.id}
                        className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                          }`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-semibold text-sm">
                              {req.tenantName || req.schoolName || `Tenant ${req.tenantId || req.id}`}
                            </p>
                            <p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                              Requested by: {req.requestedBy || req.adminEmail || req.tenantId || "—"}
                            </p>
                            {(req.message || req.reason) && (
                              <p className={`mt-1 text-xs italic line-clamp-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                "{req.message || req.reason}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleApproveCancellationRequest(req)}
                              disabled={approvingId === req.id || rejectingId === req.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaPowerOff />
                              {approvingId === req.id ? "Processing…" : "Approve cancel"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRejectCancellationRequest(req)}
                              disabled={approvingId === req.id || rejectingId === req.id}
                              className="inline-flex items-center gap-1.5 rounded-full bg-slate-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaTimesCircle />
                              {rejectingId === req.id ? "Rejecting…" : "Reject"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {prefs.showDevTracker && activePanel === "devActivityTracker" && (
              <DevActivityTracker isDarkMode={isDarkMode} />
            )}

          </div>
        </section>
      </div>

      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className={`w-full max-w-xl rounded-3xl border p-6 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Customize</p>
                <h2 className="mt-2 text-2xl font-bold">Choose super admin sections</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomize(false)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {preferenceOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => toggleDraftPref(option.key)}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${draftPrefs[option.key]
                    ? isDarkMode
                      ? "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200"
                      : "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700"
                    : isDarkMode
                      ? "border-slate-800 bg-slate-950 text-slate-300"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                >
                  <span className="font-semibold">{option.label}</span>
                  <span className={`inline-flex h-6 w-10 items-center rounded-full p-1 transition ${draftPrefs[option.key] ? "bg-fuchsia-500" : isDarkMode ? "bg-slate-800" : "bg-slate-300"}`}>
                    <span className={`h-4 w-4 rounded-full bg-white transition ${draftPrefs[option.key] ? "translate-x-4" : "translate-x-0"}`} />
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={applyCustomization}
                className="rounded-full bg-fuchsia-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-fuchsia-700"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={resetCustomization}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border border-slate-700 text-slate-200 hover:bg-slate-800" : "border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminModalShell
        isOpen={isTenantModalOpen}
        onClose={() => setIsTenantModalOpen(false)}
        isDarkMode={isDarkMode}
        maxWidth="max-w-4xl"
      >
        <form onSubmit={handleSaveTenant} className="p-6 sm:p-7">
          <div className="pr-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Tenant editor
            </p>
            <h2 className="mt-2 text-2xl font-bold">
              {tenantForm.id ? "Update tenant" : "Add new tenant"}
            </h2>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Name
              </span>
              <input
                type="text"
                value={tenantForm.adminName}
                onChange={(event) => updateTenantForm("adminName", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                placeholder="Tenant name"
                required
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                School Name
              </span>
              <input
                type="text"
                value={tenantForm.schoolName}
                onChange={(event) => updateTenantForm("schoolName", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                placeholder="Enter school name"
                required
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Admin Email
              </span>
              <input
                type="email"
                value={tenantForm.adminEmail}
                onChange={(event) => updateTenantForm("adminEmail", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                placeholder="admin@example.com"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Admin Mobile
              </span>
              <input
                type="tel"
                value={tenantForm.adminMobile}
                onChange={(event) => updateTenantForm("adminMobile", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                placeholder="9876543210"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Country
              </span>
              <input
                type="text"
                value={tenantForm.country}
                onChange={(event) => updateTenantForm("country", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
                placeholder="India"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Plan
              </span>
              <select
                value={tenantForm.plan}
                onChange={(event) => updateTenantForm("plan", event.target.value)}
                disabled={!tenantPlanOptions.length}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              >
                {!tenantPlanOptions.length && <option value="">No plan options from backend</option>}
                {tenantPlanOptions.map((option) => (
                  <option key={`tenant-plan-${option}`} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Campuses
              </span>
              <input
                type="number"
                min="0"
                value={tenantForm.campuses}
                onChange={(event) => updateTenantForm("campuses", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Admins
              </span>
              <input
                type="number"
                min="0"
                value={tenantForm.admins}
                onChange={(event) => updateTenantForm("admins", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Students
              </span>
              <input
                type="number"
                min="0"
                value={tenantForm.students}
                onChange={(event) => updateTenantForm("students", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              />
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Status
              </span>
              <select
                value={tenantForm.status}
                onChange={(event) => updateTenantForm("status", event.target.value)}
                disabled={!tenantStatusOptions.length}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              >
                {!tenantStatusOptions.length && <option value="">No status options from backend</option>}
                {tenantStatusOptions.map((option) => (
                  <option key={`tenant-status-${option}`} value={option}>{option}</option>
                ))}
              </select>
            </label>

          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={savingTenant}
              className="inline-flex items-center gap-2 rounded-full bg-fuchsia-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave /> {savingTenant ? "Saving..." : tenantForm.id ? "Save changes" : "Add tenant"}
            </button>
            <button
              type="button"
              onClick={() => setIsTenantModalOpen(false)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${isDarkMode
                  ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
            >
              Cancel
            </button>
          </div>
        </form>
      </AdminModalShell>

      <AdminModalShell
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        isDarkMode={isDarkMode}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSaveSubscription} className="p-6 sm:p-7">
          <div className="pr-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Subscription controls
            </p>
            <h2 className="mt-2 text-2xl font-bold">Plan and renewal</h2>
          </div>

          <div className="mt-5 grid gap-4">
            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Tenant</span>
              <select
                value={selectedTenantId}
                onChange={(event) => selectTenant(event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              >
                {tenants.map((tenant) => <option key={`subscription-tenant-${tenant.id}`} value={tenant.id}>{tenant.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Plan tier
              </span>
              <select
                value={subscriptionForm.plan}
                onChange={(event) => updateSubscriptionForm("plan", event.target.value)}
                disabled={!subscriptionPlanOptions.length}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              >
                {!subscriptionPlanOptions.length && <option value="">No plan options from backend</option>}
                {subscriptionPlanOptions.map((option) => (
                  <option key={`subscription-plan-${option}`} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className={`mb-1 block ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                Renewal date
              </span>
              <input
                type="date"
                value={subscriptionForm.renewalDate}
                onChange={(event) => updateSubscriptionForm("renewalDate", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:outline-none focus:ring-2 focus:ring-fuchsia-100"
              />
            </label>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={savingSubscription || !selectedTenant}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaMoneyBillWave /> {savingSubscription ? "Updating..." : "Save subscription"}
            </button>
            <button
              type="button"
              onClick={() => setIsSubscriptionModalOpen(false)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${isDarkMode
                  ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
                  : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
            >
              Cancel
            </button>
          </div>
        </form>
      </AdminModalShell>
    </div>
  );
};

export default SuperAdminSaaS;
